import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { format, differenceInHours } from "date-fns";
import { Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Button, TextField, IconButton,
    useTheme, useMediaQuery, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import useAuthCheck from 'state/useAuthCheck';
import { INVOICE_OFFSET_HOURS } from 'state/constants';
import api from 'state/axios';
// import FlexBetween from 'components/FlexBetween';
import DeleteConfirm from 'components/DeleteConfirm';
import BankTransferDialog from './BankTransferDialog';


const QuantityControl = ({ productId, type, quantity, handleQuantityChange }) => (
    <Box display="flex" alignItems="center">
        <IconButton size="small" color="primary" onClick={() => handleQuantityChange(productId, type, quantity - 1)}>
            <RemoveIcon />
        </IconButton>
        <TextField
            value={quantity}
            onChange={(e) => handleQuantityChange(productId, type, Math.max(0, parseInt(e.target.value, 10)))}
            InputProps={{ 
                inputProps: { style: { textAlign: 'center', padding: 0 } } 
            }}
            sx={{ 
                width: '36px', 
                mx: '8px', 
                '& .MuiInputBase-root': { 
                    height: '40px',
                    backgroundColor: quantity > 0 ? 'lightgreen' : 'inherit', 
                    color: quantity > 0 ? 'darkgreen' : 'inherit',           
                } 
            }}
        />
        <IconButton size="small" color="primary" onClick={() => handleQuantityChange(productId, type, quantity + 1)}>
            <AddIcon />
        </IconButton>
    </Box>
);

const InvoiceDialog = ({ 
    open, 
    onClose, 
    selectedInvoiceId, 
    crewCode, 
    crewName, 
    selectedFlights, 
    crewMembers, 
    selectedProducts, 
    onInvoiceSaved 
}) => {
    useAuthCheck();
    const user = useSelector((state) => state.auth.user);
    const params = useSelector((state) => state.auth.params);
    const theme = useTheme();
    const neutralDark = theme.palette.neutral.dark;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);    

    const [isSaving, setIsSaving] = useState(false);
    const [isProcessingTimeOut, setIsProcessingTimeOut] = useState(true);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [deleteType, setDeleteType] = useState(null); 

    const [dialogOpen, setDialogOpen] = useState(false);
        
    const [products, setProducts] = useState([]);
    const [productList, setProductList] = useState([]);
    const [quantities, setQuantities] = useState({});
    const [priceType, setPriceType] = useState('domesticprice');
        
    const [invoiceOffsetHours, setInvoiceOffsetHours] = useState(INVOICE_OFFSET_HOURS);
    
    const formatNumber = (number) => {
        return new Intl.NumberFormat().format(number);
    };

    useEffect(() => {
        const param = params?.find((item) => item.param === "INVOICE_OFFSET_HOURS");
        if (param) {
          setInvoiceOffsetHours(Number(param.value));
        }
    }, [params]);

    useEffect(() => {
        const checkFlightDate = () => {
            const now = new Date();
            const isTimeOut = selectedFlights.some(flight => {                
                return differenceInHours(now, new Date(flight.flightDate)) > invoiceOffsetHours
            });
            setIsProcessingTimeOut(isTimeOut);
        };
    
        checkFlightDate();
    }, [selectedFlights, invoiceOffsetHours]);

    const resetDialogState = useCallback(() => {
        setProducts([]);
        setProductList([]);
        setQuantities({});
        setPriceType('domesticprice');     
        setError(null);   
    }, [])

    useEffect(() => {
        const checkFlightDate = () => {
            const now = new Date();
            const isTimeOut = selectedFlights.some(flight => {                
                return differenceInHours(now, new Date(flight.flightDate)) > INVOICE_OFFSET_HOURS
            });
            setIsProcessingTimeOut(isTimeOut);
        };
    
        checkFlightDate();
    }, [selectedFlights]);

    const fetchProducts = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get('/products/active');

            const initialProducts = res.data;
            if (selectedFlights.length > 0) {
                const sectorType = selectedFlights[0].sectorType;
                
                initialProducts.forEach(product => {
                    if (sectorType === 'International') {
                        product.price = product.internationalPrice;
                    } else if (sectorType === 'Domestic') {
                        product.price = product.domesticPrice;
                    }
                });
            }
            setProducts(initialProducts);

            const finalProducts = selectedProducts || initialProducts;
            setProductList(finalProducts);

            const initialQuantities = finalProducts.reduce((acc, product) => {
                acc[product._id] = {
                    preorderQuantity: product.preorderQuantity || 0,
                    directQuantity: product.directQuantity || 0,
                };
                return acc;
            }, {});
            setQuantities(initialQuantities);
        } catch (error) {
            setError(error);
            toast.error(error.message || "Failed to fetch products!");
        } finally {
            setLoading(false);
        }
    }, [selectedFlights, selectedProducts]);

    useEffect(() => {
        if (open) fetchProducts();
        else resetDialogState();
    }, [open, fetchProducts, resetDialogState]);

    const totalAmounts = useMemo(() => {
        let preorderAmount = 0; 
        let directAmount = 0;
        let totalAmount = 0;
        let preorderCommission = 0;
        let directCommission = 0;
        let totalCommission = 0;
        let payableAmount = 0;

        productList.forEach((product) => {
            const { preorderQuantity, directQuantity } = quantities[product._id] || {};
            preorderAmount += (product.price || 0) * (preorderQuantity || 0);
            directAmount += (product.price || 0) * (directQuantity || 0);
            preorderCommission += (product.price || 0) * (preorderQuantity || 0) * (product.preorderCommissionRate || 0);
            directCommission += (product.price || 0) * (directQuantity || 0) * (product.directCommissionRate || 0);
        });
        totalAmount = preorderAmount + directAmount;
        // preorderCommission = preorderAmount * PREORDER_COMMISSION_RATE;
        // directCommission = directAmount * DIRECT_COMMISSION_RATE;
        totalCommission = preorderCommission + directCommission;
        payableAmount = directAmount - directCommission;

        return {
            preorderAmount,
            directAmount,
            totalAmount,
            payableAmount,
            preorderCommission,
            directCommission,
            totalCommission,
        };
    }, [productList, quantities]);

    const handleSaveInvoice = async () => {
        setIsSaving(true); // Disable button immediately
        // let saveError = null; 
    
        const { 
            preorderAmount,
            directAmount,
            totalAmount,
            payableAmount,
            preorderCommission,
            directCommission,
            totalCommission,
        } = totalAmounts;
    
        const crewCount = crewMembers && crewMembers.length > 0 ? crewMembers.length : 0;
        const eachCrewPreorderCommission = crewCount > 0 ? preorderCommission / crewCount : 0;
        const eachCrewDirectCommission = crewCount > 0 ? directCommission / crewCount : 0;
        const eachCrewTotalCommission = crewCount > 0 ? totalCommission / crewCount : 0;
        
        const payload = {
            invoiceId: selectedInvoiceId,
            crewCode, 
            crewName,
            selectedFlights,
            crewMembers,
            products: productList.map(product => ({
                ...product,
                preorderQuantity: quantities[product._id]?.preorderQuantity || 0,
                directQuantity: quantities[product._id]?.directQuantity || 0,
            })),
            preorderAmount,
            directAmount,
            totalAmount,
            preorderCommission,
            directCommission,
            totalCommission,
            payableAmount,
            crewCount,
            eachCrewPreorderCommission,
            eachCrewDirectCommission,
            eachCrewTotalCommission,
            userId: user.crewCode,
        };
    
        try {
            const url = selectedInvoiceId
                ? `/inflightsales/update/${payload.invoiceId}`
                : '/inflightsales/invoice';
            const method = selectedInvoiceId ? 'put' : 'post';

            await api[method](url, payload);
            onInvoiceSaved(selectedFlights);
            
            if (payableAmount > 0) {
                setDialogOpen(true)
            } else {
                onClose();
            };
        } catch (error) {
            // saveError = error; 
            setError(error);
            toast.error(error.message || "Failed to save invoice!");
        } finally {
            // if (!saveError) {
            //     onClose();
            // }
            setIsSaving(false); // Always re-enable button in finally
        }
    };
        
    const handleDeleteClick = () => {        
        setDeleteType('single');
        setConfirmDialogOpen(true);
    };
    
    const handleDeleteInvoice = async () => {
        try {
            if (selectedInvoiceId) {
                await api.delete(`/inflightsales/delete/${selectedInvoiceId}`);
                // Notify parent component or update state after deletion if needed
                onInvoiceSaved(selectedFlights);
                onClose();
            }
        } catch (error) {
            setError(error);
            toast.error("Failed to delete invoice: " + error);
        }
    };

    const handlePriceChange = (productId, price) => {
        const updatedProductList = productList.map(product => {
            if (product._id === productId) {
                return { ...product, price }; 
            }
            return product;
        });
        setProductList(updatedProductList);
    };

    const handleQuantityChange = (productId, type, newQuantity) => {
        setQuantities(prev => ({
            ...prev,
            [productId]: { 
                ...prev[productId], 
                [type]: Math.max(0, newQuantity) 
            },
        }));
    };

    const togglePriceType = () => {
        const updatedProductList = productList.map((product) => {
            const matchProduct = products.find(item => item._id === product._id);

            if (matchProduct) {
                if (priceType === 'domesticprice') {
                    product.price = matchProduct.internationalPrice;
                } else {
                    product.price = matchProduct.domesticPrice;
                }
            }
            return product;
        });
        setProductList(updatedProductList);
        setPriceType(priceType === 'domesticprice' ? 'internationalprice' : 'domesticprice');
    };
    
    const handleCloseDialog = () => {
        setPriceType('domesticprice');
        setProductList([]);
        setQuantities({});        
        onClose();
    };

    // Update the BankTransferDialog’s onClose to handle closing both dialogs
    const handleBankTransferClose = () => {
        setDialogOpen(false); // Close the child dialog
        onClose(); // Close the parent dialog after the child is closed
    };
        
    return (
        <Dialog 
            open={open} 
            onClose={handleCloseDialog}
            sx={{ '& .MuiDialog-paper': { minWidth: isMobile ? '90%' : '55%' } }}
        >
            <DialogTitle borderBottom={`1px solid ${neutralDark}`}>
                <Typography 
                    fontWeight="bold" 
                    color="primary" 
                    paddingBottom="8px"
                    borderBottom={`1px solid ${neutralDark}`}
                >
                    INVOICE: {crewCode} - {crewName}
                </Typography>
                <Box p="4px" >
                    {selectedFlights.length > 0 ? (
                        selectedFlights.map((flight, idx) => (                            
                        <Box 
                            key={idx} 
                            display="flex" 
                            justifyContent="space-between" 
                            alignItems="center"                         
                        >
                            <Typography width="20px" textAlign="center" color="primary">{flight.flightNo}</Typography>
                            <Typography width="44px" textAlign="center">{format(flight.flightDate, 'ddMMMyy')}</Typography>
                            <Typography width="25px" textAlign="center">{flight.etd}</Typography>
                            <Typography width="20px" textAlign="center">{flight.dep}</Typography>
                            <Typography width="20px" textAlign="center">{flight.arr}</Typography>
                            <Typography width="25px" textAlign="center">{flight.eta}</Typography>
                            <Typography width="4px" textAlign="center">{flight.pax}</Typography>
                        </Box>
                    ))) : (
                        <Typography color="textSecondary">No flight selected for this invoice.</Typography>
                    )}
                </Box>
                
                <Box 
                    display="flex" 
                    justifyContent="space-between" 
                    alignItems="center" 
                    padding="4px 0 0 0" 
                    borderTop={`1px solid ${neutralDark}`}
                >   
                    <Typography>
                        Preorder: {formatNumber(totalAmounts.preorderAmount)}
                    </Typography>   
                    <Typography>
                        Preorder Commission: {formatNumber(totalAmounts.preorderCommission)}
                    </Typography>                 
                </Box>   
                <Box 
                    display="flex" 
                    justifyContent="space-between" 
                    alignItems="center" 
                >   
                    <Typography>
                        Direct: {formatNumber(totalAmounts.directAmount)}
                    </Typography>                    
                    <Typography color="primary">
                        Direct Commission: {formatNumber(totalAmounts.directCommission)}
                    </Typography>
                    <Typography color="primary">
                        Payable: {formatNumber(totalAmounts.payableAmount)}
                    </Typography> 
                </Box>
            </DialogTitle>

            <DialogContent>
                {loading ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%", 
                            width: "100%",
                            mt: 1,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (productList.map((product, idx) => (
                        <Box 
                            key={idx} 
                            p="8px 0" 
                            borderBottom={`1px solid ${neutralDark}`}
                        > 
                            <Box
                                display="flex"
                                justifyContent="space-between" 
                                alignItems="center" 
                                gap={2}
                            >
                                <Typography 
                                    color="primary"
                                    textAlign="left"
                                    sx={{ width: isMobile ? '250px' : '370px' }} 
                                >
                                    {idx + 1}. {product.productName}
                                </Typography>
                                <Typography width="20px" textAlign="center">{product.unit}</Typography>
                                <TextField
                                    id={`price-${idx}`}
                                    variant="standard"
                                    type="number"
                                    label="Price"                                    
                                    name="price"                                    
                                    onChange={(e) => {
                                        handlePriceChange(product._id, Math.max(0, parseInt(e.target.value, 10))); 
                                    }}
                                    value={product.price}
                                    InputProps={{
                                        inputProps: { readOnly: true, min: 0, style: { textAlign: 'center' } } 
                                    }}
                                    sx={{ width: '80px' }}
                                />
                            </Box>
                        
                            <Box 
                                display="flex"
                                justifyContent="space-between" 
                                alignItems="center" 
                                p="4px 0"
                                gap={2}
                            >         
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography width="56px" textAlign="center">Preorder</Typography>
                                    <QuantityControl 
                                        productId={product._id} 
                                        type="preorderQuantity" 
                                        quantity={quantities[product._id]?.preorderQuantity || 0} 
                                        handleQuantityChange={handleQuantityChange} 
                                    />
                                </Box>
                                
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography width="50px" textAlign="center">Direct</Typography>
                                    <QuantityControl 
                                        productId={product._id} 
                                        type="directQuantity" 
                                        quantity={quantities[product._id]?.directQuantity || 0} 
                                        handleQuantityChange={handleQuantityChange} 
                                    />
                                </Box>
                            </Box>                        
                        </Box>
                    ))
                )}                
            </DialogContent>
            <DialogActions>             
                {error && <Typography color="error">{error?.message}</Typography>}
                {selectedInvoiceId && (
                    <Button 
                        variant="outlined" 
                        color="error" 
                        disabled={loading || isProcessingTimeOut}
                        onClick={handleDeleteClick}
                    >
                        Delete
                    </Button>
                )}   
                {products && (
                    <Button 
                        variant="outlined" 
                        disabled={loading || isProcessingTimeOut}
                        sx={{ display: 'none' }}
                        onClick={togglePriceType} 
                    >
                        {priceType === 'domesticprice' ? 'Int Price' : 'Dom Price'}
                    </Button>
                )}
                <Button 
                    variant="outlined" 
                    disabled={loading || isProcessingTimeOut || isSaving}
                    onClick={handleSaveInvoice}
                >
                    Save
                </Button>
                <Button 
                    variant="outlined" 
                    color="secondary" 
                    onClick={handleCloseDialog}
                >
                    Close
                </Button>
            </DialogActions>

            <DeleteConfirm
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={handleDeleteInvoice}
                deleteType={deleteType}
            />

            <BankTransferDialog
                open={dialogOpen}
                onClose={handleBankTransferClose} 
                crewCode={crewCode}
                selectedFlights={selectedFlights}
            />
        </Dialog>
    );
};

export default InvoiceDialog;
